import React from "react";
import {
  getShipmentDisplayStatus,
  getShipmentCurrentProgressStep,
  getShipmentProgressSteps,
  getShipmentStepIconClass,
} from "../utils/shipmentProgress";

export default function ProcessStepper({ shipment, kpiColor }) {
  const displayStatus = getShipmentDisplayStatus(shipment);
  const currentStep = getShipmentCurrentProgressStep(shipment);
  const steps = getShipmentProgressSteps(shipment).map((status, index) => ({
    label: status,
    isActive: currentStep >= index + 1,
  }));

  const getStepDate = (stepLabel) => {
    const remark = Array.isArray(shipment.remarks)
      ? shipment.remarks.find((r) => r.status === stepLabel)
      : null;
    return remark ? remark.date : null;
  };

  const fillHeight =
    currentStep === 1 || steps.length === 1
      ? 0
      : ((currentStep - 1) / (steps.length - 1)) * 100;

  const getStatusColor = (status) => {
    switch ((status || "").toLowerCase()) {
      case "order placed":
        return "bg-blue/10 text-blue border-blue/30";
      case "in warehouse":
        return "bg-yellow/10 text-blue border-yellow/30";
      case "departure at origin":
        return "bg-purple-100 text-purple-700 border-purple-300";
      case "in transit":
        return "bg-orange-100 text-orange-700 border-orange-300";
      case "arrival at destination":
        return "bg-green-100 text-green-700 border-green-300";
      case "custom clearance":
        return "bg-indigo-100 text-indigo-700 border-indigo-300";
      case "delivered":
        return "bg-emerald-100 text-emerald-700 border-emerald-300";
      case "notification":
        return "bg-cyan-100 text-cyan-700 border-cyan-300";
      case "turnover":
        return "bg-teal-100 text-teal-700 border-teal-300";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 sm:p-6 w-full shadow-lg border border-gray-100 h-full flex flex-col">
      {/* Shipment Info Card */}
      <div className={`rounded-xl p-4 mb-6 border border-gray-200 ${kpiColor || "bg-gradient-to-r from-blue/5 to-blue/10"}`}>
        <div className="flex items-center justify-between">
          <div className="w-8 h-8 bg-gradient-to-br from-blue to-blue/80 rounded-full flex items-center justify-center shadow-md">
            <i className="fas fa-box text-white text-xs"></i>
          </div>

          <div
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(
              displayStatus
            )}`}
          >
            <span className="w-1.5 h-1.5 bg-current rounded-full mr-2 animate-pulse" />
            {displayStatus}
          </div>
        </div>

        <div className="mt-3 space-y-1">
          <div className="text-blue text-sm font-semibold break-words">
            <span className="font-bold">Job Order:</span> {shipment.job_order}
          </div>

          <div className="text-blue text-xs sm:text-sm break-words">
            <span className="font-bold">Consignee:</span> {shipment.receiver_name}
          </div>

          <div className="text-blue text-xs sm:text-sm break-words">
            <span className="font-bold">Shipper:</span> {shipment.shipper_name}
          </div>
        </div>
      </div>

      {/* Timeline (flex grow + scroll) */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-blue/30 scrollbar-track-transparent">
        <div className="relative flex flex-col ml-0">
          {/* Vertical line (bg) */}
          <div className="absolute left-[19px] top-[20px] bottom-[20px] w-0.5 bg-gray-300 z-0" />
          {/* Vertical progress fill */}
          <div
            className="absolute left-[19px] top-[20px] w-0.5 bg-gradient-to-b from-blue to-blue/80 z-10 transition-all duration-500 ease-out"
            style={{ height: `calc(${fillHeight}% - 20px)` }}
          />

          {steps.map((step, index) => {
            const stepDate = getStepDate(step.label);
            return (
              <div key={index} className="relative flex items-start py-1.5">
                <div className="relative z-20 flex-shrink-0">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 shadow-sm transition-all duration-300 ${
                      step.isActive
                        ? "border-yellow bg-gradient-to-br from-blue to-blue/90"
                        : "border-gray-300 bg-white"
                    }`}
                  >
                    <i
                      className={`${getShipmentStepIconClass(step.label, shipment)} ${
                        step.isActive ? "text-white text-xs" : "text-gray-400 text-xs"
                      }`}
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full ml-3 sm:ml-4 min-w-0">
                  <div className="font-semibold text-blue text-sm break-words">{step.label}</div>
                  <div className="text-xs sm:text-sm text-blue font-medium mt-1 sm:mt-0 sm:ml-2 flex-shrink-0">
                    {stepDate
                      ? new Intl.DateTimeFormat("en-US", {
                          month: "short",
                          day: "2-digit",
                          year: "numeric",
                          timeZone: "Asia/Manila",
                        }).format(new Date(stepDate))
                      : "N/A"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer progress */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-grey">
            <i className="fas fa-info-circle" />
            <span>
              Progress: {currentStep} of {steps.length} steps completed
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue to-blue/80 transition-all duration-500"
                style={{
                  width: `${(currentStep / steps.length) * 100}%`,
                }}
              />
            </div>
            <span className="text-blue font-bold">
              {Math.round((currentStep / steps.length) * 100)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
